/* ArtMaster city cover. Original Shopify media remains the fallback. */
(() => {
  'use strict';
  if (window.CMCityCover) { window.CMCityCover.scan(document); return; }

  const ATTRIBUTE = 'data-cm-city-cover';
  const SIZE = 1080;
  const TIMEOUT = 12000;
  const renders = new Map();
  const fonts = new Map();
  let fontSerial = 0;
  const instances = new WeakMap();
  const savedAttributes = ['src', 'srcset', 'sizes', 'data-src', 'data-srcset', 'data-widths',
    'data-sizes', 'data-aspectratio', 'width', 'height', 'data-photoswipe-src',
    'data-photoswipe-width', 'data-photoswipe-height', 'alt', 'style'];
  const lazyClasses = ['lazyload', 'lazyloading', 'lazyloaded'];

  function deadline(promise, cleanup) {
    let timer;
    return Promise.race([promise, new Promise((_, reject) => {
      timer = setTimeout(() => { if (cleanup) cleanup(); reject(new Error('Asset timeout')); }, TIMEOUT);
    })]).finally(() => clearTimeout(timer));
  }

  function assetURL(value) {
    if (typeof value !== 'string' || !value || value.length > 3000) throw new Error('Invalid asset URL');
    const url = new URL(value, document.baseURI);
    const local = url.origin === location.origin;
    const shopify = /(^|\.)shopify\.com$|(^|\.)myshopify\.com$|(^|\.)shopifycdn\.(com|net)$/.test(url.hostname);
    if (url.username || url.password || (!local && !shopify) ||
      (url.protocol !== 'https:' && !(local && url.protocol === 'http:'))) throw new Error('Unsupported asset URL');
    return url.href;
  }

  function label(value, limit) {
    if (typeof value !== 'string' || !value.trim() || value.length > limit || /[\u0000-\u001f\u007f]/.test(value)) {
      throw new Error('Invalid city label');
    }
    return value.trim();
  }

  function number(value, fallback, min, max) {
    if (value === undefined) return fallback;
    if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) throw new Error('Invalid crop');
    return value;
  }

  function configuration(raw) {
    if (!raw || raw.length > 14000) throw new Error('Invalid configuration');
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value) || !/^#[0-9a-f]{6}$/i.test(value.color)) {
      throw new Error('Invalid city design');
    }
    return {
      photo: assetURL(value.photo), mask: assetURL(value.mask), city: label(value.city, 80),
      color: value.color.toLowerCase(), venue: label(value.venue, 160), address: label(value.address, 200),
      fontCity: assetURL(value.fontCity), fontAddress: assetURL(value.fontAddress),
      photoX: number(value.photoX, 50, 0, 100), photoY: number(value.photoY, 50, 0, 100),
      zoom: number(value.zoom, 1.1, 1, 2),
      labelTop: number(value.labelTop, 10, 0, 60), labelLeft: number(value.labelLeft, 10.5, 0, 40)
    };
  }

  function imageAsset(src) {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    const loaded = new Promise((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Image unavailable'));
    });
    image.src = src;
    return deadline(loaded.then(async () => {
      if (image.decode) await image.decode();
      if (!image.naturalWidth || !image.naturalHeight) throw new Error('Empty image');
      return image;
    }), () => { image.src = ''; }).finally(() => { image.onload = image.onerror = null; });
  }

  function fontAsset(url) {
    if (fonts.has(url)) return fonts.get(url);
    if (!window.FontFace || !document.fonts || fonts.size >= 8) return Promise.reject(new Error('Font unavailable'));
    const family = 'CMCityCover' + fontSerial++;
    const face = new FontFace(family, 'url(' + JSON.stringify(url) + ')');
    const pending = deadline(face.load()).then(loaded => {
      document.fonts.add(loaded);
      return family;
    }).catch(error => { fonts.delete(url); throw error; });
    fonts.set(url, pending);
    return pending;
  }

  function fittedText(context, text, family, size, x, y, width) {
    context.font = size + 'px "' + family + '"';
    const measured = context.measureText(text).width;
    if (measured > width) size *= width / measured;
    // Reject unreadable labels instead of publishing an illegible or clipped address.
    if (size < 24) throw new Error('Label exceeds available space');
    context.font = size + 'px "' + family + '"';
    context.fillText(text, x, y);
  }

  function canvasURL(canvas) {
    return deadline(new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error('Empty render')); return; }
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Render unavailable'));
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      }, 'image/png');
    }));
  }

  async function render(config) {
    const [photo, mask, cityFont, addressFont] = await Promise.all([
      imageAsset(config.photo), imageAsset(config.mask), fontAsset(config.fontCity), fontAsset(config.fontAddress)
    ]);
    if (mask.naturalWidth !== SIZE || mask.naturalHeight !== SIZE) throw new Error('Mask dimensions must be 1080 square');
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = SIZE;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas unavailable');
    const scale = Math.max(SIZE / photo.naturalWidth, SIZE / photo.naturalHeight) * config.zoom;
    const width = photo.naturalWidth * scale;
    const height = photo.naturalHeight * scale;
    context.drawImage(photo, (SIZE - width) * config.photoX / 100, (SIZE - height) * config.photoY / 100, width, height);

    const rgb = [1, 3, 5].map(offset => parseInt(config.color.slice(offset, offset + 2), 16)).join(',');
    const gradient = context.createLinearGradient(0, 0, 0, SIZE);
    gradient.addColorStop(0, config.color);
    gradient.addColorStop(.19, config.color);
    gradient.addColorStop(.34, 'rgba(' + rgb + ',.72)');
    gradient.addColorStop(.69, 'rgba(' + rgb + ',0)');
    gradient.addColorStop(1, 'rgba(' + rgb + ',0)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, SIZE, SIZE);

    const x = SIZE * config.labelLeft / 100;
    const y = SIZE * config.labelTop / 100;
    const labelWidth = Math.min(SIZE * .78, SIZE - x - 20);
    context.fillStyle = '#ffffff';
    context.textBaseline = 'top';
    context.save();
    context.translate(x, y + 10);
    context.scale(SIZE * .051 / 24, SIZE * .058 / 28);
    context.fill(new Path2D('M12 1A10 10 0 0 0 2 11c0 7 10 16 10 16s10-9 10-16A10 10 0 0 0 12 1Zm0 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z'));
    context.restore();
    fittedText(context, config.city, cityFont, SIZE * .07, x + SIZE * .071, y, labelWidth - SIZE * .071);
    const addressY = y + SIZE * (.07 * 1.15 + .017);
    fittedText(context, config.venue, addressFont, SIZE * .035, x, addressY, labelWidth);
    fittedText(context, config.address, addressFont, SIZE * .035, x, addressY + SIZE * .035 * 1.19, labelWidth);
    context.globalCompositeOperation = 'destination-in';
    context.drawImage(mask, 0, 0);
    const result = await canvasURL(canvas);
    await imageAsset(result); // Decode the finished PNG before replacing any working Shopify image.
    return result;
  }

  function composition(config) {
    const key = JSON.stringify(config);
    if (renders.has(key)) {
      const cached = renders.get(key);
      renders.delete(key); renders.set(key, cached);
      return cached;
    }
    while (renders.size >= 16) renders.delete(renders.keys().next().value);
    const pending = render(config).catch(error => {
      if (renders.get(key) === pending) renders.delete(key);
      throw error;
    });
    renders.set(key, pending);
    return pending;
  }

  function capture(image) {
    return {
      attributes: savedAttributes.map(name => [name, image.getAttribute(name)]),
      lazy: lazyClasses.filter(name => image.classList.contains(name)),
      zoom: image.classList.contains('photoswipe__image') || image.hasAttribute('data-photoswipe-src')
    };
  }

  function restore(image, entry) {
    if (!entry.applied) return;
    for (const [name, value] of entry.original.attributes) {
      if (value === null) image.removeAttribute(name); else image.setAttribute(name, value);
    }
    lazyClasses.forEach(name => image.classList.toggle(name, entry.original.lazy.includes(name)));
    entry.applied = false;
  }

  function current(image, entry) {
    if (!image.isConnected) { entry.status = 'idle'; return false; }
    return instances.get(image) === entry && image.getAttribute(ATTRIBUTE) === entry.raw;
  }

  function refreshGallery(image) {
    const gallery = image.closest('[data-product-photos]');
    if (!gallery || !window.Flickity || typeof window.Flickity.data !== 'function') return;
    requestAnimationFrame(() => {
      if (!gallery.isConnected) return;
      const slideshow = window.Flickity.data(gallery);
      if (slideshow && typeof slideshow.resize === 'function') slideshow.resize();
    });
  }

  function process(image) {
    const raw = image.getAttribute(ATTRIBUTE);
    let previous = instances.get(image);
    if (previous && previous.raw === raw && (previous.status === 'ready' || previous.status === 'loading')) return;
    if (previous) restore(image, previous);
    const entry = {raw, original: previous ? previous.original : capture(image), status: 'loading', applied: false};
    instances.set(image, entry);
    if (!raw) { image.removeAttribute('data-cm-city-cover-status'); refreshGallery(image); return; }
    image.setAttribute('data-cm-city-cover-status', 'loading');
    refreshGallery(image);
    let config;
    try { config = configuration(raw); } catch {
      entry.status = 'fallback'; image.setAttribute('data-cm-city-cover-status', 'fallback'); refreshGallery(image); return;
    }
    composition(config).then(src => {
      if (!current(image, entry)) return;
      // Prevent a later lazysizes pass from restoring the uncomposed source.
      image.setAttribute('data-src', src);
      image.setAttribute('data-srcset', src);
      image.removeAttribute('data-widths');
      image.setAttribute('data-aspectratio', '1');
      image.removeAttribute('sizes');
      image.setAttribute('srcset', src);
      image.setAttribute('src', src);
      image.setAttribute('width', String(SIZE));
      image.setAttribute('height', String(SIZE));
      // Motion may constrain width through max-width while leaving the HTML height intact.
      image.style.height = 'auto';
      const originalAlt = entry.original.attributes.find(([name]) => name === 'alt')[1];
      image.setAttribute('alt', [originalAlt || 'Workshop participants', config.city, config.venue, config.address].join(' — '));
      image.classList.remove('lazyload', 'lazyloading');
      image.classList.add('lazyloaded');
      if (entry.original.zoom) {
        image.setAttribute('data-photoswipe-src', src);
        image.setAttribute('data-photoswipe-width', String(SIZE));
        image.setAttribute('data-photoswipe-height', String(SIZE));
      }
      entry.applied = true;
      entry.status = 'ready';
      image.setAttribute('data-cm-city-cover-status', 'ready');
      refreshGallery(image);
      image.dispatchEvent(new CustomEvent('cm:city-cover:ready', {bubbles: true}));
    }).catch(() => {
      if (!current(image, entry)) return;
      entry.status = 'fallback';
      image.setAttribute('data-cm-city-cover-status', 'fallback');
      refreshGallery(image);
    });
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches('img[' + ATTRIBUTE + ']')) process(root);
    root.querySelectorAll('img[' + ATTRIBUTE + ']').forEach(process);
  }

  window.CMCityCover = {scan};
  const observer = new MutationObserver(records => {
    records.forEach(record => {
      if (record.type === 'attributes' && record.target.tagName === 'IMG') process(record.target);
      else record.addedNodes.forEach(scan);
    });
  });
  observer.observe(document.documentElement, {subtree: true, childList: true, attributes: true, attributeFilter: [ATTRIBUTE]});
  document.addEventListener('shopify:section:load', event => scan(event.target));
  scan(document);
})();
